import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import CartDrawer from "@/components/CartDrawer";
import CookieBanner from "@/components/CookieBanner";
import WhatsAppFab from "@/components/WhatsAppFab";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import CustomerLogin from "@/pages/CustomerLogin";
import CustomerRegister from "@/pages/CustomerRegister";
import CustomerAccount from "@/pages/CustomerAccount";

import RefundPolicy from "@/pages/policies/RefundPolicy";
import PrivacyPolicy from "@/pages/policies/PrivacyPolicy";
import TermsPolicy from "@/pages/policies/TermsPolicy";
import ShippingPolicy from "@/pages/policies/ShippingPolicy";
import LegalNotice from "@/pages/policies/LegalNotice";
import CookiePolicy from "@/pages/policies/CookiePolicy";
import TrackOrder from "@/pages/TrackOrder";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminActivityLog from "@/pages/admin/AdminActivityLog";
import AdminTracking from "@/pages/admin/AdminTracking";
import AdminAbandonedCarts from "@/pages/admin/AdminAbandonedCarts";
import SuperAdminGuard from "@/components/admin/SuperAdminGuard";
import BrandLanding from "@/pages/BrandLanding";
import SupplierRegister from "@/pages/SupplierRegister";
import MetaPixel from "@/components/MetaPixel";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import PromoUrlHandler from "@/components/PromoUrlHandler";
import ExitIntentPopup from "@/components/ExitIntentPopup";

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
      <AuthProvider>
        <CustomerAuthProvider>
          <LanguageProvider>
            <CartProvider>
              <CartDrawer />
              <CookieBanner />
              <WhatsAppFab />
              <Toaster position="top-right" richColors />
              <MetaPixel />
              <GoogleAnalytics />
              <PromoUrlHandler />
              <ExitIntentPopup />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/catalogo" element={<Catalog />} />
                <Route path="/accessori" element={<Catalog accessoriesOnly={true} />} />
                <Route path="/prodotto/:id" element={<ProductDetail />} />
                <Route path="/marca/:brand" element={<BrandLanding />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/checkout/cancel" element={<Checkout />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogArticle />} />
                <Route path="/chi-siamo" element={<About />} />
                <Route path="/contatti" element={<Contact />} />

                <Route path="/rimborsi" element={<RefundPolicy />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/termini" element={<TermsPolicy />} />
                <Route path="/spedizioni" element={<ShippingPolicy />} />
                <Route path="/note-legali" element={<LegalNotice />} />
                <Route path="/cookie" element={<CookiePolicy />} />
                <Route path="/traccia-ordine" element={<TrackOrder />} />

                <Route path="/login" element={<CustomerLogin />} />
                <Route path="/registrati" element={<CustomerRegister />} />
                <Route path="/fornitori/registrati" element={<SupplierRegister />} />
                <Route path="/account" element={<CustomerAccount />} />

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/customers" element={<AdminCustomers />} />
                <Route path="/admin/suppliers" element={<AdminSuppliers />} />
                <Route path="/admin/blog" element={<SuperAdminGuard><AdminBlog /></SuperAdminGuard>} />
                <Route path="/admin/homepage" element={<SuperAdminGuard><AdminHomepage /></SuperAdminGuard>} />
                <Route path="/admin/reviews" element={<SuperAdminGuard><AdminReviews /></SuperAdminGuard>} />
                <Route path="/admin/coupons" element={<SuperAdminGuard><AdminCoupons /></SuperAdminGuard>} />
                <Route path="/admin/promo-codes" element={<SuperAdminGuard><AdminCoupons /></SuperAdminGuard>} />
                <Route path="/admin/settings" element={<AdminSettings />} />
                <Route path="/admin/team" element={<SuperAdminGuard><AdminTeam /></SuperAdminGuard>} />
                <Route path="/admin/activity-log" element={<SuperAdminGuard><AdminActivityLog /></SuperAdminGuard>} />
                <Route path="/admin/tracking" element={<SuperAdminGuard><AdminTracking /></SuperAdminGuard>} />
                <Route path="/admin/abandoned-carts" element={<AdminAbandonedCarts />} />
              </Routes>
            </CartProvider>
          </LanguageProvider>
        </CustomerAuthProvider>
      </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
